import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getUserData } from '../pages/auth/utils/firebase/userOperations';

// Simple in-memory cache
// Structure: { [userId]: { friends: [], timestamp: number } }
const friendsCache = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useCachedFriends = (currentUser) => {
    const [friends, setFriends] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchFriends = async () => {
            if (!currentUser?.uid) {
                setFriends([]);
                setLoading(false);
                return;
            }

            // Check cache first
            const cachedData = friendsCache[currentUser.uid];
            if (cachedData && (Date.now() - cachedData.timestamp < CACHE_DURATION)) {
                setFriends(cachedData.friends);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                let validFriends = [];

                // Try to query conversations (may fail if index is missing)
                try {
                    const conversationsRef = collection(db, 'conversations');
                    const q = query(
                        conversationsRef,
                        where('memberIds', 'array-contains', currentUser.uid),
                        orderBy('lastMessageAt', 'desc'),
                        limit(20) // Increased limit to find more unique friends
                    );

                    const snapshot = await getDocs(q);
                    const conversationsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                    // Extract unique friend IDs from conversations
                    const friendIdsSet = new Set();
                    conversationsList.forEach(conv => {
                        if (Array.isArray(conv.memberIds)) {
                            conv.memberIds.forEach(id => {
                                if (id !== currentUser.uid) {
                                    friendIdsSet.add(id);
                                }
                            });
                        }
                    });

                    // Fetch user data for each friend
                    const friendIds = Array.from(friendIdsSet).slice(0, 10); // Limit to top 10 recent friends
                    const friendsData = await Promise.all(
                        friendIds.map(async (friendId) => {
                            try {
                                const userData = await getUserData(friendId);
                                return userData;
                            } catch (err) {
                                console.error(`Error fetching friend data for ${friendId}:`, err);
                                return null;
                            }
                        })
                    );

                    validFriends = friendsData.filter(friend => friend !== null);
                } catch (convError) {
                    console.warn('Error fetching conversations (may need Firestore index):', convError);
                    // Continue to suggestions even if conversations query fails
                }

                // If no recent friends, fetch suggestions from users collection
                if (validFriends.length === 0) {
                    try {
                        const usersRef = collection(db, 'users');
                        let suggestions = [];

                        // Try orderBy first (same as search)
                        try {
                            const usersQ = query(usersRef, orderBy('username_lower'), limit(50));
                            const usersSnapshot = await getDocs(usersQ);
                            suggestions = usersSnapshot.docs
                                .map(doc => ({ id: doc.id, ...doc.data() }))
                                .filter(u => u.id !== currentUser.uid)
                                .slice(0, 5);
                        } catch (orderError) {
                            console.warn('orderBy query failed, using fallback:', orderError);
                            // Fallback: get users without orderBy
                            const fallbackQ = query(usersRef, limit(100));
                            const fallbackSnap = await getDocs(fallbackQ);
                            suggestions = fallbackSnap.docs
                                .map(doc => ({ id: doc.id, ...doc.data() }))
                                .filter(u => u.id !== currentUser.uid)
                                .slice(0, 5);
                        }

                        validFriends = suggestions;
                    } catch (err) {
                        console.error('Error fetching suggestions:', err);
                    }
                }

                // Update state and cache
                setFriends(validFriends);
                friendsCache[currentUser.uid] = {
                    friends: validFriends,
                    timestamp: Date.now()
                };

            } catch (error) {
                console.error('Error in fetchFriends:', error);
                setError(error.message || 'Failed to load friends');
                setFriends([]);
            } finally {
                setLoading(false);
            }
        };

        fetchFriends();
    }, [currentUser?.uid]);

    // Force refresh function if needed
    const refreshFriends = () => {
        if (currentUser?.uid) {
            delete friendsCache[currentUser.uid];
        }
    };

    return { friends, loading, error, refreshFriends };
};
